import { useMemo, useState } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import Seo from '@/components/feature/Seo';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import { hotArticles, formatHeat, categoryName } from '@/utils/content';
import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import TrendList from './components/TrendList';
import CategoriesRow from './components/CategoriesRow';
import ContrastShow from './components/ContrastShow';
import CommentsWall from './components/CommentsWall';
import FaqBlock from './components/FaqBlock';
import CtaStrip from './components/CtaStrip';

export default function Home() {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState<string>('all');

  const trending = hotArticles(6);
  const top = trending[0];
  const restTrend = trending.slice(1);

  const hotList = hotArticles();
  const filtered = useMemo(() => {
    let list = articles;
    if (query.trim()) {
      const q = query.trim();
      list = list.filter(
        (a) =>
          a.title.includes(q) ||
          a.excerpt.includes(q) ||
          a.tags.some((t) => t.includes(q)) ||
          categoryName(a.categoryId).includes(q),
      );
    }
    if (activeCat !== 'all') list = list.filter((a) => a.categoryId === activeCat);
    return list;
  }, [query, activeCat]);

  const handleSearch = (kw: string) => {
    setActiveCat('all');
    setQuery(kw);
    document.getElementById('feed')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <PageLayout>
      <Seo
        title="校园爆料 · 校园风云人物与社团名场面盘点"
        description="校园爆点聚焦校园风云人物爆料，盘点班长学委秘密合集、校园社团名场面、毕业照黑历史与校园人物反差瞬间，这里都有料，满足你对校园红人的好奇。"
        keywords="校园爆料,校园风云人物爆料,班长学委秘密合集,校园社团名场面,校园人物反差瞬间"
      />

      <Hero onSearch={handleSearch} />

      {/* 今日热搜 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_0.85fr]">
          <div className="rounded-2xl border border-cream-200 bg-white p-5 md:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-xl text-ink md:text-2xl">
                <i className="ri-fire-fill text-primary-500" />
                今日热搜榜
              </h2>
              <span className="text-xs text-ink-600">每 30 分钟更新一次热度</span>
            </div>
            <TrendList items={restTrend} />
          </div>

          <Link
            to={`/article/${top.id}`}
            className="group relative block overflow-hidden rounded-2xl border-4 border-white"
          >
            <img
              src={top.cover}
              alt={top.coverAlt}
              className="h-full min-h-[340px] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-ink">热度第一</span>
              <h3 className="mt-3 text-xl font-bold leading-snug text-white md:text-2xl">{top.title}</h3>
              <p className="mt-2 line-clamp-2 text-sm text-cream-100">{top.excerpt}</p>
              <span className="mt-3 inline-flex items-center gap-2 text-sm text-cream-100">
                <i className="ri-fire-fill text-accent-300" />
                {formatHeat(top.heat)} 热度
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      <CategoriesRow />

      {/* 最新信息流 */}
      <section id="feed" data-anchor className="bg-cream-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="font-display text-2xl text-ink md:text-3xl">
                {query ? `“${query}” 的搜索结果` : '最新爆料 · 信息流'}
              </h2>
              <p className="mt-1 text-sm text-ink-600">
                {query ? `为你找到 ${filtered.length} 条相关内容` : '最新最热的校园盘点都在这里'}
              </p>
            </div>
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full bg-cream-200 px-4 py-2 text-sm font-medium text-ink hover:bg-cream-300"
              >
                <i className="ri-close-line" /> 清除搜索
              </button>
            )}
          </div>

          <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="栏目筛选">
            <button
              type="button"
              role="tab"
              aria-selected={activeCat === 'all'}
              onClick={() => setActiveCat('all')}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeCat === 'all'
                  ? 'bg-primary-500 font-bold text-cream-50'
                  : 'bg-white text-ink-600 hover:bg-primary-100'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={activeCat === c.id}
                onClick={() => setActiveCat(c.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  activeCat === c.id
                    ? 'bg-primary-500 font-bold text-cream-50'
                    : 'bg-white text-ink-600 hover:bg-primary-100'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-cream-300 bg-cream-50 p-12 text-center">
              <i className="ri-emotion-sad-line text-4xl text-ink-700" />
              <p className="mt-3 text-ink-600">暂时没搜到相关内容，换个关键词试试？</p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setActiveCat('all');
                }}
                className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm font-bold text-cream-50 hover:bg-primary-600"
              >
                查看全部内容
              </button>
            </div>
          )}
        </div>
      </section>

      <ContrastShow />
      <CommentsWall />
      <FaqBlock />
      <CtaStrip />
    </PageLayout>
  );
}